import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { finalize, forkJoin, map, of, switchMap } from 'rxjs';
import type { DettaglioDocumentoI } from '../models/interfaces/documenti-adempimenti/dettaglio-documento.interface';
import type { DestinatariNuovoInvioGetResI } from '../models/interfaces/documenti-adempimenti/richiedi-nuovo-invio.interface';
import type { ResRiepilogoDettaglioNuovoInvioI } from '../models/interfaces/documenti-adempimenti/riepilogo-dettaglio-richiesta-nuovo-invio.interface';
import { DocumentiAdempimentiGestioneDocumentiService } from '../services/documenti-adempimenti-gestione-documenti.service';
import { RichiesteNuovoInvioService } from '../services/richieste-nuovo-invio.service';
import { SpinnerService } from '../services/spinner-service';

export interface RitrasmettiRichiestaNuovoInvioResolved {
  richiesta: ResRiepilogoDettaglioNuovoInvioI | null;
  dettaglioDocumento: DettaglioDocumentoI | null;
  destinatari: DestinatariNuovoInvioGetResI;
}

const EMPTY_DESTINATARI: DestinatariNuovoInvioGetResI = {
  emailPecEnte: '',
  destinatari: [],
};

/**
 * Carica richiesta, dettaglio documento e destinatari prima del mount della pagina ritrasmetti.
 * Stesso pattern di `dettaglioDocumentoResolver` su richiedi nuovo invio.
 */
export const ritrasmettiRichiestaNuovoInvioResolver: ResolveFn<RitrasmettiRichiestaNuovoInvioResolved> = (
  route,
) => {
  const richiesteService = inject(RichiesteNuovoInvioService);
  const documentiService = inject(DocumentiAdempimentiGestioneDocumentiService);
  const spinnerService = inject(SpinnerService);
  const sequRichiesta = Number(route.paramMap.get('sequRichiesta'));

  if (!Number.isFinite(sequRichiesta) || sequRichiesta <= 0) {
    return of({
      richiesta: null,
      dettaglioDocumento: null,
      destinatari: EMPTY_DESTINATARI,
    });
  }

  spinnerService.enableSpinner();
  return richiesteService.getRiepilogoDettaglio(sequRichiesta).pipe(
    switchMap((richiesta) => {
      if (!richiesta?.sequDocumento) {
        return of({
          richiesta,
          dettaglioDocumento: null,
          destinatari: EMPTY_DESTINATARI,
        });
      }

      return forkJoin({
        dettaglioDocumento: documentiService.getDettaglioDocumento(richiesta.sequDocumento),
        destinatari: richiesteService.getDestinatari(richiesta.sequDocumento),
      }).pipe(
        map(({ dettaglioDocumento, destinatari }) => ({
          richiesta,
          dettaglioDocumento,
          destinatari,
        })),
      );
    }),
    finalize(() => spinnerService.disableSpinner()),
  );
};
